import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { validateMobile } from "../../utils/helpers";

const CreditScoreModal2 = ({ isOpen, onClose, answers, score }) => {
  const navigate = useNavigate();

  // Helper to get API URL
  const getApiUrl = (endpoint) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocal ? 'http://localhost:8001' : '';
    return `${baseUrl}${endpoint}`;
  };

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm();

  const mobile = watch("mobile");
  const otp = watch("otp");

  // Handle OTP sending via Karix
  const handleSendOtp = async () => {
    if (!validateMobile(mobile)) {
      toast.error("Enter a valid mobile number before requesting OTP");
      return;
    }
    setIsSendingOtp(true);
    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: mobile }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success("OTP sent successfully!");
      } else {
        toast.error(data.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.error("OTP Send error:", error);
      toast.error("Error sending OTP.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    const verifyToast = toast.loading("Verifying OTP...");

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: mobile, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success("OTP verified successfully!", { id: verifyToast });
      } else {
        toast.error(data.message || "Invalid or expired OTP", { id: verifyToast });
      }
    } catch (error) {
      console.error("OTP Verify error:", error);
      toast.error("An error occurred during verification.", { id: verifyToast });
    }
  };

  const onSubmit = async (data) => {
    if (!otpVerified) {
      toast.error("Please verify your mobile number with OTP");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Submitting your enquiry...");

    try {
      const response = await fetch(getApiUrl('/api/credit-score-2/submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, answers, score })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || "Enquiry submitted successfully!", { id: loadingToast });
        navigate('/thank-you');
        onClose();
      } else {
        toast.error(result.message || "Failed to submit enquiry.", { id: loadingToast });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred during submission.", { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setOtpSent(false);
    setOtpVerified(false);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, y: 50, opacity: 0 }}
          animate={{
            scale: 1,
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 300, damping: 20 },
          }}
          exit={{ opacity: 0, scale: 0.9, y: 50 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-3xl shadow-2xl overflow-hidden border border-indigo-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold">Instant Credit Check</h2>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 max-h-[75vh] overflow-y-auto">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name (as per PAN) *
                </label>
                <input
                  {...register("name", { required: "Name is required" })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <div className="flex space-x-2">
                  <input
                    {...register("mobile", {
                      required: "Mobile is required",
                      validate: (v) => validateMobile(v) || "Enter valid 10-digit number",
                    })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${otpVerified ? "border-green-500 bg-green-50" : ""}`}
                    placeholder="Enter mobile"
                    maxLength={10}
                    disabled={otpVerified}
                  />
                  {!otpVerified && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp || !mobile || mobile.length < 10}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${isSendingOtp || !mobile || mobile.length < 10
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                    >
                      {isSendingOtp ? "Sending..." : otpSent ? "Resend" : "Send OTP"}
                    </button>
                  )}
                  {otpVerified && (
                    <div className="flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Verified
                    </div>
                  )}
                </div>
                {errors.mobile && (
                  <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
                )}
              </div>

              {/* OTP */}
              {otpSent && !otpVerified && (
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                  <label className="block text-sm font-semibold text-indigo-800 mb-2">
                    Enter OTP sent to your mobile
                  </label>
                  <div className="flex space-x-2">
                    <input
                      {...register("otp", { required: "OTP required" })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-center text-lg tracking-widest"
                      placeholder="6-digit OTP"
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={!otp || otp.length < 6}
                      className={`px-6 py-2 rounded-xl font-medium transition-all ${!otp || otp.length < 6
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                      Verify
                    </button>
                  </div>
                </div>
              )}

              {/* Profession */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Profession *
                </label>
                <select
                  {...register("profession", { required: "Select profession" })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select</option>
                  <option value="salaried">Salaried</option>
                  <option value="self-employed">Self-Employed</option>
                </select>
                {errors.profession && (
                  <p className="text-red-500 text-sm mt-1">{errors.profession.message}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City *
                </label>
                <input
                  {...register("city", { required: "City required" })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter city"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State *
                </label>
                <select
                  {...register("state", { required: "State required" })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Telangana">Telangana</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:opacity-95 transition-all"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreditScoreModal2;
